import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { WorkoutExercise, WorkoutExerciseCreate, WorkoutExerciseUpdate, ExerciseLogCreate, ExerciseLogUpdate, ExerciseLog, Client } from '../types';

export const useWorkoutExercises = (workoutId: number, workout?: any, selectedClient?: Client | null) => {
  const queryClient = useQueryClient();

  // Мутация для добавления упражнения в тренировку
  const addExerciseMutation = useMutation(
    (data: WorkoutExerciseCreate) => apiService.addExerciseToWorkout(workoutId, data),
    {
      onSuccess: () => {
        toast.success('Упражнение добавлено в тренировку!');
        // Инвалидируем кэш для обновления статуса выполнения
        queryClient.invalidateQueries(['workout', workoutId]);
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при добавлении упражнения';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для обновления упражнения в тренировке
  const updateExerciseMutation = useMutation(
    ({ exerciseId, data }: { exerciseId: number; data: WorkoutExerciseUpdate }) =>
      apiService.updateWorkoutExercise(workoutId, exerciseId, data),
    {
      onSuccess: (updatedExercise) => {
        toast.success('Упражнение обновлено!');
        // Не инвалидируем все запросы, так как локальное состояние будет обновлено отдельно
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении упражнения';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для удаления упражнения из тренировки
  const deleteExerciseMutation = useMutation(
    (exerciseId: number) => apiService.deleteWorkoutExercise(workoutId, exerciseId),
    {
      onSuccess: () => {
        toast.success('Упражнение удалено из тренировки!');
        queryClient.invalidateQueries(['workout', workoutId]);
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении упражнения';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для создания лога упражнения
  const createExerciseLogMutation = useMutation(
    ({ exerciseId, data }: { exerciseId: number; data: ExerciseLogCreate }) =>
      apiService.createExerciseLog(workoutId, exerciseId, data),
    {
      onSuccess: (createdLog) => {
        toast.success('Упражнение назначено клиенту!');
        // Инвалидируем кэш для обновления статуса выполнения
        queryClient.invalidateQueries(['workout', workoutId]);
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при назначении упражнения клиенту';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для удаления лога упражнения
  const deleteExerciseLogMutation = useMutation(
    (logId: number) => apiService.deleteExerciseLog(logId),
    {
      onSuccess: () => {
        toast.success('Упражнение удалено!');
        // Инвалидируем кэш для обновления статуса выполнения
        queryClient.invalidateQueries(['workout', workoutId]);
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении упражнения';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для обновления лога упражнения
  const updateExerciseLogMutation = useMutation(
    ({ logId, data, suppressToast = false }: { logId: number; data: ExerciseLogUpdate; suppressToast?: boolean }) =>
      apiService.updateExerciseLog(logId, data),
    {
      onSuccess: (updatedLog, variables) => {
        // Если подавлен toast, не показываем сообщения
        if (variables.suppressToast) {
          return;
        }
        
        // Проверяем, является ли это обновлением статуса выполнения
        const isCompletionUpdate = 'is_completed' in variables.data && Object.keys(variables.data).length === 1;
        
        // Проверяем, является ли это обновлением параметров упражнения (sets, reps, weight)
        const isParameterUpdate = ('completed_sets' in variables.data || 'completed_reps' in variables.data || 'actual_weight' in variables.data) && 
                                 !('is_completed' in variables.data);
        
        if (isCompletionUpdate) {
          // Для обновления статуса выполнения инвалидируем кэш клиентов
          toast.success('Статус упражнения обновлен!');
          queryClient.invalidateQueries('clients');
        } else if (isParameterUpdate) {
          // Для обновления параметров упражнения не инвалидируем все запросы
          // Локальное состояние будет обновлено отдельно
          toast.success('Параметры упражнения обновлены!');
        } else {
          // Для других обновлений инвалидируем связанные запросы
          toast.success('Лог упражнения обновлен!');
          queryClient.invalidateQueries(['workout', workoutId]);
          queryClient.invalidateQueries(['workouts']);
          queryClient.invalidateQueries('clients');
          if (selectedClient) {
            queryClient.invalidateQueries(['client-exercise-order', workoutId, selectedClient.id]);
          }
        }
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении лога упражнения';
        toast.error(errorMessage);
      }
    }
  );

  // Функции для управления порядком упражнений
  const moveExerciseUp = async (logId: number) => {
    if (!workout || !selectedClient) return;
    
    try {
      // Получаем текущий порядок упражнений
      const currentOrder = await apiService.getClientExerciseOrder(workout.id, selectedClient.id);
      console.log('Current order from API (up):', currentOrder);
      
      const currentIndex = currentOrder.findIndex(ex => ex.log_id === logId);
      console.log('Current index (up):', currentIndex, 'for logId:', logId);
      
      if (currentIndex === -1) {
        toast.error('Упражнение не найдено');
        return;
      }
      
      if (currentIndex === 0) {
        toast.error('Упражнение уже в начале списка');
        return;
      }
      
      // Меняем местами с предыдущим упражнением
      const prevExercise = currentOrder[currentIndex - 1];
      const currentExercise = currentOrder[currentIndex];
      
      console.log('Swapping exercises (up):', {
        current: { log_id: currentExercise.log_id, order_index: currentExercise.order_index },
        prev: { log_id: prevExercise.log_id, order_index: prevExercise.order_index }
      });
      
      await apiService.updateClientExerciseOrderBulk(workout.id, selectedClient.id, [
        { log_id: currentExercise.log_id, order_index: prevExercise.order_index },
        { log_id: prevExercise.log_id, order_index: currentExercise.order_index }
      ]);
      
      // Инвалидируем кэш для обновления статуса выполнения
      queryClient.invalidateQueries(['workout', workoutId]);
      queryClient.invalidateQueries('clients');
    } catch (error: any) {
      console.error('Error in moveExerciseUp:', error);
      const errorMessage = error.detail || 'Ошибка при изменении порядка упражнений';
      toast.error(errorMessage);
    }
  };

  const moveExerciseDown = async (logId: number) => {
    if (!workout || !selectedClient) return;
    
    try {
      // Получаем текущий порядок упражнений
      const currentOrder = await apiService.getClientExerciseOrder(workout.id, selectedClient.id);
      console.log('Current order from API:', currentOrder);
      
      const currentIndex = currentOrder.findIndex(ex => ex.log_id === logId);
      console.log('Current index:', currentIndex, 'for logId:', logId);
      console.log('Total exercises:', currentOrder.length);
      
      if (currentIndex === -1) {
        toast.error('Упражнение не найдено');
        return;
      }
      
      if (currentIndex === currentOrder.length - 1) {
        toast.error('Упражнение уже в конце списка');
        return;
      }
      
      // Меняем местами со следующим упражнением
      const currentExercise = currentOrder[currentIndex];
      const nextExercise = currentOrder[currentIndex + 1];
      
      console.log('Swapping exercises:', {
        current: { log_id: currentExercise.log_id, order_index: currentExercise.order_index },
        next: { log_id: nextExercise.log_id, order_index: nextExercise.order_index }
      });
      
      await apiService.updateClientExerciseOrderBulk(workout.id, selectedClient.id, [
        { log_id: currentExercise.log_id, order_index: nextExercise.order_index },
        { log_id: nextExercise.log_id, order_index: currentExercise.order_index }
      ]);
      
      // Инвалидируем кэш для обновления статуса выполнения
      queryClient.invalidateQueries(['workout', workoutId]);
      queryClient.invalidateQueries('clients');
    } catch (error: any) {
      console.error('Error in moveExerciseDown:', error);
      const errorMessage = error.detail || 'Ошибка при изменении порядка упражнений';
      toast.error(errorMessage);
    }
  };

  return {
    addExercise: addExerciseMutation.mutate,
    updateExercise: updateExerciseMutation.mutate,
    deleteExercise: deleteExerciseMutation.mutate,
    createExerciseLog: createExerciseLogMutation.mutate,
    deleteExerciseLog: deleteExerciseLogMutation.mutate,
    updateExerciseLog: updateExerciseLogMutation.mutate,
    moveExerciseUp,
    moveExerciseDown,
    isAdding: addExerciseMutation.isLoading,
    isUpdating: updateExerciseMutation.isLoading,
    isDeleting: deleteExerciseMutation.isLoading,
    isCreatingLog: createExerciseLogMutation.isLoading
  };
}; 